import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { paymentAPI, userAPI } from '../services/api';
import { pushToDataLayer, clearEcommerce } from '../utils/dataLayer';
import { useUser } from '../contexts/UserContext';
import { formatPrice } from '@shared/utils';

interface LocationState {
  name: string;
  amount: number;
  giftCardUsed: boolean;
  type?: 'subscription' | 'session_bundle' | 'package_pack';
  itemId?: string;
  customerEmail?: string;
  customerPhone?: string;
  coupon?: string;
}

const PurchaseConfirmationPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const [searchParams] = useSearchParams();
  const isSubscription = state?.type === 'subscription';
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [contractError, setContractError] = useState<string | null>(null);
  const contractCreatedRef = useRef(false);

  useDocumentTitle(t.confirmation.title);

  const hasTrackedPurchase = useRef(false);
  const { user } = useUser();
  const [customerPhone, setCustomerPhone] = useState<string>('');

  useEffect(() => {
    const paymentIntentId = searchParams.get('payment_intent');
    const itemType = searchParams.get('itemType') as
      | 'subscription'
      | 'session_bundle'
      | 'package_pack'
      | null;
    const itemId = searchParams.get('itemId');

    if (
      paymentIntentId &&
      itemType &&
      itemId &&
      paymentIntentId !== 'gift_card_payment' &&
      !contractCreatedRef.current
    ) {
      contractCreatedRef.current = true;
      const createContract = async () => {
        setIsCreatingContract(true);
        setContractError(null);
        try {
          await paymentAPI.createContractAfterPayment({ paymentIntentId, itemType, itemId });
        } catch (error) {
          console.error('Failed to create contract:', error);
          setContractError(
            t.confirmation.contractError ||
              'There was an issue activating your session bundle. Please contact support.'
          );
          contractCreatedRef.current = false;
        } finally {
          setIsCreatingContract(false);
        }
      };
      createContract();
    }
  }, [searchParams, t.confirmation.contractError]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await userAPI.getProfile();
        if (response.data.phoneNumber) {
          setCustomerPhone(response.data.phoneNumber);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const paymentIntentId = searchParams.get('payment_intent');
    const itemId = searchParams.get('itemId') || state?.itemId;
    const {
      amount,
      name,
      type,
      customerEmail: stateEmail,
      customerPhone: statePhone,
      coupon,
    } = state || {};

    if (
      !hasTrackedPurchase.current &&
      (paymentIntentId || itemId) &&
      amount !== undefined
    ) {
      hasTrackedPurchase.current = true;
      clearEcommerce();

      const email = stateEmail || user?.email || '';
      const phone = statePhone || customerPhone || '';

      pushToDataLayer('purchase', {
        content: {
          category:
            type === 'subscription'
              ? 'subscription'
              : type === 'package_pack'
                ? 'package_pack'
                : 'session_bundle',
        },
        ecommerce: {
          transaction_id: paymentIntentId || itemId,
          value: amount,
          currency: 'EUR',
          ...(coupon && { coupon }),
          items: [
            {
              item_id: itemId,
              item_name: name,
              item_category:
                type === 'subscription'
                  ? 'Subscription'
                  : type === 'package_pack'
                    ? 'Package Pack'
                    : 'Session Bundle',
              value: amount,
            },
          ],
        },
        customer: {
          email,
          phone,
        },
      });
    }
  }, [searchParams, state, user, customerPhone]);

  if (!state) {
    return (
      <div className="container mx-auto max-w-4xl p-4 md:p-8">
        <div className="py-10 text-center">
          <p className="mb-4 text-gray-600">{t.confirmation.noData}</p>
          <button onClick={() => navigate('/')} className="text-blue-600 hover:underline">
            {t.common.goHome}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl p-4 md:p-8">
      <div className="rounded-lg bg-white p-8 text-center shadow-lg">
        <div className="mb-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-3xl font-bold text-gray-900">{t.confirmation.successTitle}</h1>
          <p className="text-lg text-gray-600">{t.confirmation.successMessage}</p>
        </div>

        <div className="mb-6 rounded-md bg-gray-50 p-6">
          <h2 className="mb-4 text-lg font-semibold">{t.confirmation.orderDetails}</h2>
          <div className="mx-auto max-w-sm space-y-2 text-left">
            <div className="flex justify-between">
              <span className="text-gray-600">
                {t.confirmation[isSubscription ? 'subscription' : 'sessionBundle']}:
              </span>
              <span className="font-medium">{state.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t.confirmation.amountPaid}:</span>
              <span className="font-medium">{formatPrice(state.amount)}</span>
            </div>
            {state.giftCardUsed && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t.confirmation.paymentMethod}:</span>
                <span className="font-medium">
                  {state.amount === 0
                    ? t.confirmation.giftCardOnly
                    : t.confirmation.cardAndGiftCard}
                </span>
              </div>
            )}
          </div>
        </div>

        {isCreatingContract && (
          <div className="py-4 text-center text-gray-600">
            <p>{t.confirmation.creatingContract || 'Finalizing your purchase, please wait...'}</p>
          </div>
        )}
        {contractError && (
          <div className="rounded-md bg-red-50 p-3 py-4 text-center text-red-600">
            <p>{contractError}</p>
          </div>
        )}

        <div className="space-y-3">
          <h3 className="mb-2 text-lg font-semibold">{t.confirmation.whatNext}</h3>
          <p className="mb-4 text-gray-600">{t.confirmation.confirmationEmail}</p>

          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <button
              onClick={() => navigate('/book')}
              className="rounded-md bg-green-600 px-6 py-3 font-medium text-white transition-colors hover:bg-green-700"
            >
              {t.confirmation.bookSession}
            </button>
            <button
              onClick={() => navigate('/account')}
              className="rounded-md bg-gray-600 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-700"
            >
              {t.confirmation.goToAccount}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseConfirmationPage;
